// Module Gestion Landing Page - Interface Admin
import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FiSave, FiEye, FiImage, FiType, FiLink, FiCheckCircle
} from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

// Types
interface LandingPageContent {
  hero: {
    title: string;
    subtitle: string;
    ctaText: string;
    backgroundImage: string;
  };
  features: {
    title: string;
    description: string;
    icon: string;
  }[];
  about: {
    title: string;
    description: string;
  };
  contact: {
    email: string;
    phone: string;
    address: string;
  };
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div``;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  ${props => {
    if (props.variant === 'primary') {
      return `
        background: ${theme.colors.primary};
        color: white;
        &:hover {
          background: ${theme.colors.primary}DD;
        }
      `;
    }
    return `
      background: ${theme.colors.surface};
      color: ${theme.colors.text};
      &:hover {
        background: ${theme.colors.surface}DD;
      }
    `;
  }}
`;

const EditorSection = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  svg {
    color: ${theme.colors.primary};
  }
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  min-height: 120px;
  resize: vertical;
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xl};
  text-align: center;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primary}05;
  }
`;

const ImagePlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.md};
  color: ${theme.colors.text}60;

  svg {
    font-size: 48px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
`;

const FeatureCard = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.surface};
`;

const AddFeatureButton = styled.button`
  padding: ${theme.spacing.lg};
  background: transparent;
  border: 2px dashed ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text}60;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    background: ${theme.colors.primary}05;
  }
`;

const SavedIndicator = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.success}10;
  color: ${theme.colors.success};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
`;

export const LandingPageManagement: React.FC = () => {
  const [isSaved, setIsSaved] = useState(false);

  const [content, setContent] = useState<LandingPageContent>({
    hero: {
      title: 'Devenez propriétaire sans apport',
      subtitle: 'Kajom Capital finance votre accession à la propriété par location-vente. Un modèle innovant soutenu par FONSIS pour démocratiser l\'habitat au Sénégal.',
      ctaText: 'Simuler mon projet',
      backgroundImage: '/hero-bg.jpg'
    },
    features: [
      {
        title: 'Scoring Intelligent',
        description: 'Grâce à notre système de scoring intelligent et équitable, nous évaluons votre dossier de manière transparente et objective',
        icon: '📊'
      },
      {
        title: 'Commissions Digitales',
        description: 'Organisation et gestion des commissions d\'attribution en ligne',
        icon: '👥'
      },
      {
        title: 'Suivi Candidat',
        description: 'Interface dédiée pour les candidats avec suivi en temps réel',
        icon: '📱'
      }
    ],
    about: {
      title: 'À propos de P-Screen',
      description: 'P-Screen by Pradium Tech est une solution innovante qui digitalise et optimise le processus d\'attribution des logements sociaux. Notre plateforme garantit équité, transparence et efficacité dans la gestion des candidatures.'
    },
    contact: {
      email: 'contact@pradiumtech.com',
      phone: '+33 1 23 45 67 89',
      address: 'Paris, France'
    }
  });

  const handleSave = () => {
    // Save logic here
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
    alert('Modifications enregistrées avec succès');
  };

  const handlePreview = () => {
    window.open('/candidate-landing', '_blank');
  };

  const updateHero = (field: keyof typeof content.hero, value: string) => {
    setContent({
      ...content,
      hero: { ...content.hero, [field]: value }
    });
  };

  const updateAbout = (field: keyof typeof content.about, value: string) => {
    setContent({
      ...content,
      about: { ...content.about, [field]: value }
    });
  };

  const updateContact = (field: keyof typeof content.contact, value: string) => {
    setContent({
      ...content,
      contact: { ...content.contact, [field]: value }
    });
  };

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <HeaderLeft>
            <Title>Gestion de la Landing Page Candidat</Title>
            <Subtitle>Personnalisez le contenu de la page d'accueil candidat (Kajom Capital)</Subtitle>
          </HeaderLeft>
          <ActionButtons>
            {isSaved && (
              <SavedIndicator>
                <FiCheckCircle /> Enregistré
              </SavedIndicator>
            )}
            <Button onClick={handlePreview}>
              <FiEye /> Prévisualiser
            </Button>
            <Button variant="primary" onClick={handleSave}>
              <FiSave /> Enregistrer
            </Button>
          </ActionButtons>
        </Header>

        {/* Section Hero */}
        <EditorSection>
          <SectionTitle>
            <FiImage /> Section Hero (Bannière principale)
          </SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>
                <FiType /> Titre principal
              </Label>
              <Input
                type="text"
                value={content.hero.title}
                onChange={(e) => updateHero('title', e.target.value)}
                placeholder="Titre accrocheur..."
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <FiType /> Sous-titre
              </Label>
              <Textarea
                value={content.hero.subtitle}
                onChange={(e) => updateHero('subtitle', e.target.value)}
                placeholder="Description courte..."
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <FiType /> Texte du bouton CTA
              </Label>
              <Input
                type="text"
                value={content.hero.ctaText}
                onChange={(e) => updateHero('ctaText', e.target.value)}
                placeholder="Ex: Commencer, S'inscrire..."
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <FiImage /> Image de fond
              </Label>
              <ImageUploadArea>
                <ImagePlaceholder>
                  <FiImage />
                  <div>Cliquez pour choisir une image</div>
                  <div style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.text + '40' }}>
                    Format recommandé : 1920x1080px, JPG ou PNG
                  </div>
                </ImagePlaceholder>
              </ImageUploadArea>
            </FormGroup>
          </FormGrid>
        </EditorSection>

        {/* Section Fonctionnalités */}
        <EditorSection>
          <SectionTitle>
            ⚡ Fonctionnalités principales
          </SectionTitle>
          <FeaturesGrid>
            {content.features.map((feature, index) => (
              <FeatureCard key={index}>
                <FormGrid>
                  <FormGroup>
                    <Label>Icône</Label>
                    <Input
                      type="text"
                      value={feature.icon}
                      placeholder="Emoji ou icône..."
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Titre</Label>
                    <Input
                      type="text"
                      value={feature.title}
                      placeholder="Nom de la fonctionnalité..."
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Description</Label>
                    <Textarea
                      value={feature.description}
                      placeholder="Description courte..."
                    />
                  </FormGroup>
                </FormGrid>
              </FeatureCard>
            ))}
            <AddFeatureButton>
              <FiType /> Ajouter une fonctionnalité
            </AddFeatureButton>
          </FeaturesGrid>
        </EditorSection>

        {/* Section À Propos */}
        <EditorSection>
          <SectionTitle>
            <FiType /> Section À Propos
          </SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Titre</Label>
              <Input
                type="text"
                value={content.about.title}
                onChange={(e) => updateAbout('title', e.target.value)}
                placeholder="Titre de la section..."
              />
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <Textarea
                value={content.about.description}
                onChange={(e) => updateAbout('description', e.target.value)}
                placeholder="Présentez votre plateforme..."
              />
            </FormGroup>
          </FormGrid>
        </EditorSection>

        {/* Section Contact */}
        <EditorSection>
          <SectionTitle>
            <FiLink /> Informations de Contact
          </SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={content.contact.email}
                onChange={(e) => updateContact('email', e.target.value)}
                placeholder="contact@exemple.com"
              />
            </FormGroup>

            <FormGroup>
              <Label>Téléphone</Label>
              <Input
                type="tel"
                value={content.contact.phone}
                onChange={(e) => updateContact('phone', e.target.value)}
                placeholder="+33 1 23 45 67 89"
              />
            </FormGroup>

            <FormGroup>
              <Label>Adresse</Label>
              <Input
                type="text"
                value={content.contact.address}
                onChange={(e) => updateContact('address', e.target.value)}
                placeholder="Ville, Pays"
              />
            </FormGroup>
          </FormGrid>
        </EditorSection>
      </Container>
    </DashboardLayout>
  );
};
